import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Link2, Linkedin, Facebook } from "lucide-react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/components/tour/TourProvider";
import { GrantTeaser } from "@/components/GrantTeaser";
import { API, api } from "@/lib/api";
import { CASE_TEXT } from "@/lib/landing_text";
import { toast } from "sonner";

const AMOUNTS = [["2006", 278, "SpaceX"], ["2008", 1600, "SpaceX"], ["2009", 465, "Tesla"], ["2014", 1300, "Tesla"]];

export const useLandingLang = () => (useTour().lang === "es" ? "es" : "en");
export const grantsCta = (user) => (user ? "/dashboard/grants" : `/login?mode=register&next=${encodeURIComponent("/dashboard/grants")}`);

export function Rich({ parts, strong = "text-white" }) {
  return parts.map((p, i) => (i % 2 ? <b key={i} className={strong}>{p}</b> : <span key={i}>{p}</span>));
}

const fmt = (m) => (m >= 1000 ? `$${(m / 1000).toFixed(1)}B` : `$${m}M`);

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const m = payload[0].payload;
  return <div className="bg-slate-800 border border-slate-600 rounded px-3 py-2 text-xs text-white">{m.year} · {m.who} · {m.label}: {fmt(m.amount)}</div>;
}

function ShareBar({ T, lang }) {
  const page = (src) => `${API}/share/funding?lang=${lang}&s=${src}`;
  const track = (source) => api.post("/public/share-event", { type: "share", source, lang }).catch(() => {});
  const open = (src, url) => { track(src); window.open(url, "_blank", "noopener,noreferrer,width=640,height=560"); };
  const copy = () => { track("copy"); navigator.clipboard.writeText(page("copy")); toast.success(T.copied); };
  const btn = "h-9 px-3 rounded-full border border-slate-600 text-slate-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-800 transition-colors";
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="share-funding-bar">
      <span className="text-xs text-slate-400 mr-1">{T.share}</span>
      <button className={btn} onClick={() => open("linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(page("linkedin"))}`)} data-testid="share-linkedin-button"><Linkedin className="h-3.5 w-3.5" />LinkedIn</button>
      <button className={btn} onClick={() => open("facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(page("facebook"))}`)} data-testid="share-facebook-button"><Facebook className="h-3.5 w-3.5" />Facebook</button>
      <button className={btn} onClick={copy} data-testid="share-copy-button"><Link2 className="h-3.5 w-3.5" />{T.copy}</button>
      <a className={btn} href={`${API}/share/funding-card.png?lang=${lang}`} download={`GrantVerify_funding_card_${lang}.png`} target="_blank" rel="noreferrer" data-testid="share-download-button"><Download className="h-3.5 w-3.5" />{T.download}</a>
    </div>
  );
}

function MilestoneChart({ T, lang }) {
  const data = useMemo(() => AMOUNTS.map(([year, amount, who], i) => ({ year, amount, who, label: T.labels[i] })), [T]);
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-5" data-testid="case-study-chart">
      <div className="flex justify-between items-baseline">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-400">{T.chartTitle}</div>
        <div className="text-[11px] text-slate-500">{T.millions}</div>
      </div>
      <div className="h-48 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 22, right: 4, left: -18, bottom: 0 }}>
            <XAxis dataKey="year" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#334155" }} tickLine={false} />
            <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip content={<Tip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
            <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
              {data.map((m) => <Cell key={m.year} fill={m.who === "SpaceX" ? "#10b981" : "#6ee7b7"} />)}
              <LabelList dataKey="label" position="top" fill="#cbd5e1" fontSize={10} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-700 flex items-baseline gap-3">
        <span className="font-display text-4xl font-extrabold text-white" data-testid="case-study-total">$38B+</span>
        <span className="text-xs text-slate-400 leading-snug">{T.totalNote}</span>
      </div>
      <ShareBar T={T} lang={lang} />
    </div>
  );
}

export function FundingCaseStudy() {
  const { user } = useAuth();
  const lang = useLandingLang();
  const T = CASE_TEXT[lang];
  return (
    <section className="bg-slate-950 text-white border-b border-slate-800" data-testid="case-study-section" data-no-translate lang={lang}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="eyebrow mb-3 text-emerald-400">{T.eyebrow}</div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight max-w-3xl" data-testid="case-study-title">{T.title[0]} <span className="text-emerald-400">{T.title[1]}</span></h2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 space-y-5">
            <p className="text-lg leading-relaxed text-slate-300"><Rich parts={T.p1} /></p>
            <p className="leading-relaxed text-slate-400"><Rich parts={T.p2} strong="text-slate-200" /></p>
            <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-200 font-medium" data-testid="case-study-quote">{T.quote}</blockquote>
            <MilestoneChart T={T} lang={lang} />
          </div>
          <div className="lg:col-span-6 flex flex-col gap-5">
            {T.cards.map(([t, d]) => (
              <div key={t} className="bg-slate-900 border border-slate-700 rounded-lg p-6 card-lift" data-testid="case-study-card">
                <h4 className="font-display text-lg font-bold text-emerald-400">{t}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-300"><Rich parts={d} /></p>
              </div>
            ))}
            <div className="bg-emerald-500 text-slate-950 rounded-lg p-6" data-testid="case-study-card-grantverify">
              <h4 className="font-display text-lg font-extrabold">{T.gvTitle}</h4>
              <p className="mt-2 text-sm leading-relaxed font-medium text-slate-900"><Rich parts={T.gv} strong="" /></p>
            </div>
            <GrantTeaser />
            <Link to={grantsCta(user)} data-testid="case-study-cta-button" className="self-start max-w-full">
              <Button className="h-auto min-h-12 py-3 whitespace-normal text-left px-6 sm:px-7 rounded-full bg-white text-slate-950 hover:bg-emerald-100 text-base">{T.cta} <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
        <p className="mt-12 text-[11px] leading-relaxed text-slate-500 max-w-4xl" data-testid="case-study-sources">{T.sources}</p>
      </div>
    </section>
  );
}
